import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  KeyRound,
  Loader2,
  Mail,
  PoundSterling,
  ShieldCheck,
  LogOut,
  Pencil,
  Search as SearchIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";
import EditListingModal from "@/components/EditListingModal";

/**
 * Iteration 99 — Internal admin dashboard.
 *
 * Houses the waitlist + platform-fee CSV exports that previously lived
 * on the Parent Dashboard. Gated behind an ADMIN_API_KEY prompt so
 * regular parents on `/dashboard/parent` see a clean UI.
 *
 * The key is cached in sessionStorage under the same `mttApp:adminKey`
 * name that the historical export flow used, so existing bookmarks +
 * scripts keep working.
 */
export default function AdminDashboard() {
  const navigate = useNavigate();

  // Key state — `unlocked` flips to true once a JSON fetch with the key
  // returns 200. All admin data is fetched behind that gate.
  const [adminKey, setAdminKey] = useState(() =>
    (typeof window !== "undefined" && sessionStorage.getItem("mttApp:adminKey")) || ""
  );
  const [keyInput, setKeyInput] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  // Waitlist state
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // Platform fee state
  const [feeSummary, setFeeSummary] = useState(null); // {total_platform_fees_pennies, total_transactions}
  const [feeExporting, setFeeExporting] = useState(false);
  const [feeExportError, setFeeExportError] = useState("");
  const [feeSummaryError, setFeeSummaryError] = useState("");

  // Iteration 160 — Admin Listings edit console.
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsError, setListingsError] = useState("");
  const [listingSearch, setListingSearch] = useState("");
  const [editing, setEditing] = useState(null);

  // -----------------------------------------------------------------
  // 1) Unlock flow — validate the key by calling any admin endpoint.
  //    We use `/admin/platform-fees` because it's cheap and returns
  //    JSON without side-effects.
  // -----------------------------------------------------------------
  const attemptUnlock = useCallback(async (candidateKey) => {
    if (!candidateKey) return false;
    setUnlocking(true);
    setUnlockError("");
    try {
      const { data } = await api.get("/admin/platform-fees", {
        headers: { "X-Admin-Key": candidateKey },
      });
      sessionStorage.setItem("mttApp:adminKey", candidateKey);
      setAdminKey(candidateKey);
      setUnlocked(true);
      setFeeSummary(data || null);
      return true;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) sessionStorage.removeItem("mttApp:adminKey");
      setUnlockError(
        status === 401
          ? "That admin key wasn't recognised. Try again."
          : (formatApiError(e) || "Couldn't validate key — try again.")
      );
      return false;
    } finally {
      setUnlocking(false);
    }
  }, []);

  // Auto-attempt unlock on mount if we have a cached key.
  useEffect(() => {
    if (adminKey && !unlocked) attemptUnlock(adminKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    const ok = await attemptUnlock(trimmed);
    if (ok) setKeyInput("");
  };

  const lockAndReset = () => {
    sessionStorage.removeItem("mttApp:adminKey");
    setAdminKey("");
    setUnlocked(false);
    setFeeSummary(null);
    setWaitlistCount(null);
  };

  // -----------------------------------------------------------------
  // 2) Data loaders — only run once unlocked.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!unlocked) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/waitlist/count");
        if (!cancelled) setWaitlistCount(data?.total ?? 0);
      } catch {
        if (!cancelled) setWaitlistCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [unlocked]);

  const refreshFeeSummary = useCallback(async () => {
    if (!adminKey) return;
    setFeeSummaryError("");
    try {
      const { data } = await api.get("/admin/platform-fees", {
        headers: { "X-Admin-Key": adminKey },
      });
      setFeeSummary(data || null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        lockAndReset();
        return;
      }
      setFeeSummaryError(formatApiError(e) || "Failed to refresh summary.");
    }
  }, [adminKey]);

  // Iteration 160 — Admin Listings loader.
  const loadListings = useCallback(async () => {
    if (!adminKey) return;
    setListingsLoading(true);
    setListingsError("");
    try {
      const { data } = await api.get("/admin/listings", {
        headers: { "X-Admin-Key": adminKey },
      });
      setListings(Array.isArray(data) ? data : []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) { lockAndReset(); return; }
      setListingsError(formatApiError(e) || "Couldn't load listings.");
      setListings([]);
    } finally {
      setListingsLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    if (unlocked) loadListings();
  }, [unlocked, loadListings]);

  // Iteration 288 — Org verification queue (approve/reject from the UI).
  const [orgs, setOrgs] = useState([]);
  const [orgsError, setOrgsError] = useState("");
  const [orgBusy, setOrgBusy] = useState("");
  const loadOrgs = useCallback(async () => {
    if (!adminKey) return;
    setOrgsError("");
    try {
      const { data } = await api.get("/admin/org-accounts", { headers: { "X-Admin-Key": adminKey } });
      setOrgs(data?.accounts || []);
    } catch (e) {
      if (e?.response?.status === 401) { lockAndReset(); return; }
      setOrgsError(formatApiError(e) || "Couldn't load organization accounts.");
    }
  }, [adminKey]);
  useEffect(() => {
    if (unlocked) loadOrgs();
  }, [unlocked, loadOrgs]);
  const decideOrg = async (kidId, verified) => {
    setOrgBusy(kidId);
    try {
      await api.post(`/admin/kids/${kidId}/verify-org`, { verified }, { headers: { "X-Admin-Key": adminKey } });
      await loadOrgs();
    } catch (e) {
      setOrgsError(formatApiError(e) || "Couldn't update the organization.");
    } finally {
      setOrgBusy("");
    }
  };

  const filteredListings = useMemo(() => {
    const q = listingSearch.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter((l) => {
      const hay = `${l.title || ""} ${l.kid_name || ""} ${l.id || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [listings, listingSearch]);

  // -----------------------------------------------------------------
  // 3) Export handlers
  // -----------------------------------------------------------------
  const exportWaitlist = async () => {
    setExportError("");
    setExporting(true);
    try {
      const resp = await api.get("/waitlist/export", {
        headers: { "X-Admin-Key": adminKey },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mytabletopapp-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) lockAndReset();
      setExportError(status === 401 ? "Admin key rejected — please unlock again." : (formatApiError(e) || "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const exportPlatformFees = async () => {
    setFeeExportError("");
    setFeeExporting(true);
    try {
      const resp = await api.get("/admin/platform-fees", {
        headers: { "X-Admin-Key": adminKey },
        params: { format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mytabletopapp-platform-fees-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      refreshFeeSummary();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) lockAndReset();
      setFeeExportError(status === 401 ? "Admin key rejected — please unlock again." : (formatApiError(e) || "Export failed."));
    } finally {
      setFeeExporting(false);
    }
  };

  // -----------------------------------------------------------------
  // 4) Render — gate FIRST, then dashboard.
  // -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brandTeal/5" data-testid="admin-dashboard-page">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brandSlate transition mb-6"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-brandSlate text-white">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brandSlate">Owner Console</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900">Admin Dashboard</h1>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-8 max-w-2xl">
          Internal-only tools for platform revenue reporting and marketing exports. Every action here
          is gated by the shared admin key.
        </p>

        {!unlocked ? (
          <div className="card-base p-6 md:p-8 max-w-lg" data-testid="admin-unlock-card">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandPink mb-1">Restricted area</div>
            <h2 className="font-display text-2xl font-semibold text-brandSlate">Enter the admin key</h2>
            <p className="text-sm text-slate-500 mt-1 mb-5">
              This route is not accessible to standard parent accounts. Contact the owner if you
              believe you should have access.
            </p>
            <form onSubmit={handleUnlockSubmit} className="space-y-3" data-testid="admin-unlock-form">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">Admin key</label>
              <input
                type="password"
                autoFocus
                autoComplete="off"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Paste the ADMIN_API_KEY"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-brandPink focus:ring-2 focus:ring-brandPink/20 outline-none transition font-mono text-sm"
                data-testid="admin-unlock-input"
                disabled={unlocking}
              />
              {unlockError && (
                <div className="text-xs text-rose-600" data-testid="admin-unlock-error">{unlockError}</div>
              )}
              <button
                type="submit"
                disabled={unlocking || !keyInput.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brandSlate text-white font-semibold text-sm hover:bg-brandSlateDark transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-unlock-btn"
              >
                {unlocking ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                ) : (
                  <><KeyRound className="w-4 h-4" /> Unlock admin console</>
                )}
              </button>
            </form>
            <Link to="/dashboard/parent" className="block mt-4 text-xs text-slate-500 hover:text-brandSlate">
              ← Return to the Parent Dashboard
            </Link>
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-content">
            {/* Waitlist card */}
            <div className="card-base p-6 bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/8 border-brandTeal/20" data-testid="waitlist-admin-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandTeal/15 text-brandTeal flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandTeal">App Store Waitlist</div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="font-display text-4xl font-semibold text-brandSlate" data-testid="waitlist-count-value">
                        {waitlistCount === null ? "–" : waitlistCount}
                      </span>
                      <span className="text-sm text-slate-500">
                        {waitlistCount === 1 ? "parent signed up" : "parents signed up"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Refreshes on page load.</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    type="button"
                    onClick={exportWaitlist}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brandSlate text-white font-semibold text-sm hover:bg-brandSlateDark transition disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="waitlist-export-btn"
                  >
                    {exporting ? (
                      <><KeyRound className="w-4 h-4 animate-pulse" /> Exporting…</>
                    ) : (
                      <><Download className="w-4 h-4" /> Export Waitlist CSV</>
                    )}
                  </button>
                  {exportError && (
                    <div className="text-xs text-rose-600 mt-1" data-testid="waitlist-export-error">{exportError}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Platform Fee Ledger card */}
            <div className="card-base p-6 bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/8 border-brandTeal/20" data-testid="platform-fees-admin-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandPink/15 text-brandPink flex-shrink-0">
                    <PoundSterling className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandPink">Platform Fee Ledger</div>
                    <div className="flex items-baseline gap-6 mt-1 flex-wrap">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fees collected</div>
                        <span className="font-display text-3xl font-semibold text-brandSlate" data-testid="platform-fees-total-value">
                          {feeSummary ? formatGBP((feeSummary.total_platform_fees_pennies || 0) / 100) : "—"}
                        </span>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Transactions</div>
                        <span className="font-display text-3xl font-semibold text-brandSlate" data-testid="platform-fees-tx-count">
                          {feeSummary ? (feeSummary.total_transactions ?? 0) : "—"}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Lifetime revenue across every completed marketplace sale.</div>
                    {feeSummaryError && (
                      <div className="text-xs text-rose-600 mt-1" data-testid="platform-fees-summary-error">{feeSummaryError}</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    type="button"
                    onClick={exportPlatformFees}
                    disabled={feeExporting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brandPink text-white font-semibold text-sm hover:bg-brandPinkDark transition disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="platform-fees-export-btn"
                  >
                    {feeExporting ? (
                      <><KeyRound className="w-4 h-4 animate-pulse" /> Exporting…</>
                    ) : (
                      <><Download className="w-4 h-4" /> Download Platform Fee Ledger (CSV)</>
                    )}
                  </button>
                  {feeExportError && (
                    <div className="text-xs text-rose-600 mt-1" data-testid="platform-fees-export-error">{feeExportError}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-2 flex items-center gap-4 flex-wrap">
              <Link
                to="/admin/csr-pins"
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brandTeal transition"
                data-testid="admin-csr-pins-link"
              >
                <KeyRound className="w-3.5 h-3.5" /> Manage CSR access PINs
              </Link>
              <button
                type="button"
                onClick={lockAndReset}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600 transition"
                data-testid="admin-lock-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Lock admin console
              </button>
            </div>

            {/* Iteration 160 — Admin Listings edit console. Full-edit
                access to every listing on the platform. */}
            <div className="card-base p-6" data-testid="admin-listings-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandSlate">Listings</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Full edit access across every household. Saves stamp <code className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">edited_by_admin</code> for audit.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="search"
                      value={listingSearch}
                      onChange={(e) => setListingSearch(e.target.value)}
                      placeholder="Search title, kid, id…"
                      className="pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-brandSlate w-64"
                      data-testid="admin-listings-search"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={loadListings}
                    disabled={listingsLoading}
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 hover:border-brandSlate transition disabled:opacity-50"
                    data-testid="admin-listings-refresh"
                  >
                    {listingsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
                  </button>
                </div>
              </div>

              {listingsError && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 mb-3" data-testid="admin-listings-error">
                  {listingsError}
                </div>
              )}

              {listingsLoading ? (
                <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : filteredListings.length === 0 ? (
                <div className="text-slate-500 text-sm" data-testid="admin-listings-empty">
                  {listings.length === 0 ? "No listings on the platform yet." : "No listings match that search."}
                </div>
              ) : (
                <div className="space-y-2 max-h-[540px] overflow-y-auto pr-1" data-testid="admin-listings-list">
                  {filteredListings.map((l) => (
                    <div
                      key={l.id}
                      className="grid grid-cols-12 gap-3 items-center rounded-2xl border border-slate-200 bg-white px-3 py-2.5"
                      data-testid={`admin-listing-row-${l.id}`}
                    >
                      <div className="col-span-1">
                        {l.photos && l.photos[0] ? (
                          <img src={l.photos[0]} alt="" className="w-10 h-10 rounded-xl object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-slate-100" />
                        )}
                      </div>
                      <div className="col-span-6 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate" title={l.title}>{l.title || "Untitled"}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {l.kid_name || "—"} · {l.category || "—"} · {l.status || "—"} {l.edited_by_admin ? "· edited by admin" : ""}
                        </div>
                      </div>
                      <div className="col-span-2 text-right text-sm font-semibold text-brandPinkDark">
                        {formatGBP(l.price ?? 0)}
                      </div>
                      <div className="col-span-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing(l)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl bg-brandSlate text-white hover:opacity-90 transition"
                          data-testid={`admin-listing-edit-${l.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Full edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Iteration 288 — Org verification queue */}
            <div className="card-base p-6" data-testid="admin-org-queue">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-xl font-medium text-slate-900">Organization verification queue</h2>
                <button
                  type="button"
                  onClick={loadOrgs}
                  className="text-xs font-bold text-brandSlate hover:underline"
                  data-testid="admin-org-refresh"
                >
                  Refresh
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Pending Causes: <span className="font-bold text-amber-700" data-testid="admin-org-pending-count">{orgs.filter((o) => o.org_status === "pending_verification").length}</span> awaiting review. Approving unlocks the "Verified PTA / Cause" badge and bank-only payouts.
              </p>
              {orgsError && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 mb-3" data-testid="admin-org-error">{orgsError}</div>
              )}
              {orgs.length === 0 && !orgsError && (
                <div className="text-slate-500 text-sm" data-testid="admin-org-empty">No organization sub-accounts yet.</div>
              )}
              <div className="space-y-2" data-testid="admin-org-list">
                {orgs.map((o) => (
                  <div
                    key={o.kid_id}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 flex-wrap"
                    data-testid={`admin-org-row-${o.kid_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{o.name || o.kid_id}</div>
                      <div className="text-[11px] text-slate-500">
                        Charity/URN: {o.charity_number || "—"}
                        {o.charity_api_verified ? " · register-confirmed" : ""}
                        {o.org_bank_registered ? " · bank on file" : ""}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${o.org_status === "verified" ? "bg-teal-100 text-teal-800 border-teal-200" : o.org_status === "rejected" ? "bg-rose-100 text-rose-800 border-rose-200" : "bg-amber-100 text-amber-900 border-amber-200"}`}
                      data-testid={`admin-org-status-${o.kid_id}`}
                    >
                      {o.org_status === "verified" ? "Verified" : o.org_status === "rejected" ? "Rejected" : "Pending Verification"}
                    </span>
                    {o.org_status !== "verified" && (
                      <button
                        type="button"
                        onClick={() => decideOrg(o.kid_id, true)}
                        disabled={orgBusy === o.kid_id}
                        className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                        data-testid={`admin-org-approve-${o.kid_id}`}
                      >
                        Approve & Verify
                      </button>
                    )}
                    {o.org_status !== "rejected" && (
                      <button
                        type="button"
                        onClick={() => decideOrg(o.kid_id, false)}
                        disabled={orgBusy === o.kid_id}
                        className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 disabled:opacity-60"
                        data-testid={`admin-org-reject-${o.kid_id}`}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {editing && (
              <EditListingModal
                listing={editing}
                adminKey={adminKey}
                onClose={() => setEditing(null)}
                onSaved={(updated) => {
                  setEditing(null);
                  if (updated?.id) {
                    setListings((prev) => prev.map((l) => l.id === updated.id ? { ...l, ...updated, edited_by_admin: true } : l));
                  } else {
                    loadListings();
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
